/**
 * My Trips — one-time welcome card asking what excites the user about Jody.
 *
 * Successor of the sunset waitlist's post-signup segmentation chips: the
 * same question, asked at the same "just starting" moment, but for a real
 * account (option list revised Aug 2026). Placing it here rather than
 * inside the signup form keeps signup friction-free AND catches Google
 * Sign-In users, who never see the signup form at all.
 *
 * Selection rules: at most 3 picks ("what excites you THE MOST"); once 3
 * are selected the remaining chips are disabled. The 'other' chip counts
 * toward the cap and reveals a small free-text box (persisted as
 * `User.interestsOther`). The backend mirrors both rules
 * (routes/users.js: VALID_INTERESTS, MAX_INTERESTS).
 *
 * Persistence: `PATCH /api/users/me/interests`, synced fire-and-forget
 * with a 2s debounce so a burst of toggles/typing lands as a single
 * request holding the final state. A pending sync is flushed immediately
 * on unmount so navigating away never loses the last change.
 *
 * Visibility: renders only while the user's interests are empty and the
 * card was never dismissed (localStorage). After a save, the card stays up
 * for further tweaks in this session; the next app load hides it because
 * GET /auth/me then returns a non-empty list.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/context';
import { updateMyInterests } from '../../auth/api';
import { useToast } from '../../../hooks/useToast';

const DISMISSED_KEY = 'jody_interests_card_dismissed';

// What excites them THE MOST: hard cap, mirrored by the backend.
const MAX_PICKS = 3;

// Same ids as the backend's VALID_INTERESTS (routes/users.js).
const INTEREST_OPTIONS = [
  { id: 'slow_travel', emoji: '🌺', label: 'Slow travel' },
  { id: 'night_trains', emoji: '😴', label: 'Night trains' },
  { id: 'easy_planning', emoji: '🪄', label: 'Easy trip planning' },
  { id: 'low_carbon_travel', emoji: '🌱', label: 'Low carbon travel' },
  { id: 'low_cost_trips', emoji: '💸', label: 'Low cost trips' },
  { id: 'personalized_trips', emoji: '✨', label: 'Personalized trip suggestions' },
  { id: 'friend_trips', emoji: '👫', label: 'Trips with friends' },
  { id: 'other', emoji: '✏️', label: 'Other' },
];

function chipStyle(selected: boolean, disabled: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 9999,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: disabled ? 'default' : 'pointer',
    border: `1.5px solid ${selected ? '#157a55' : 'rgba(20,40,30,0.10)'}`,
    background: selected ? '#e7f1ec' : '#f4f6f3',
    color: selected ? '#0e5c40' : '#6c7872',
    opacity: disabled ? 0.45 : 1,
    transition: 'all 0.15s',
    userSelect: 'none',
  };
}

interface PendingState {
  interests: string[];
  otherText: string;
}

const InterestsWelcomeCard: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();

  // Computed once per mount: hidden forever after a dismiss, and hidden on
  // any visit where the profile already carries interests.
  const [visible, setVisible] = useState<boolean>(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return false;
    return !user?.interests || user.interests.length === 0;
  });
  const [selected, setSelected] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const [saved, setSaved] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the not-yet-synced state while the debounce timer runs, so
  // unmount can flush exactly what the user last picked/typed.
  const pendingRef = useRef<PendingState | null>(null);

  const persist = useCallback(
    async (state: PendingState) => {
      pendingRef.current = null;
      try {
        await updateMyInterests(state.interests, state.otherText || undefined);
        setSaved(true);
        showToast({ type: 'success', title: 'Saved', message: 'Thanks for letting us know.' });
      } catch {
        showToast({ type: 'error', title: 'Error', message: 'Could not save your picks. They will not be kept.' });
      }
    },
    [showToast]
  );

  const queueSync = useCallback(
    (state: PendingState) => {
      pendingRef.current = state;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => persist(state), 2000);
    },
    [persist]
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const isSelected = prev.includes(id);
      // At the cap, only deselection is allowed (the chip is disabled in
      // the UI too; this guards re-renders racing a fast double-click).
      if (!isSelected && prev.length >= MAX_PICKS) return prev;
      const next = isSelected ? prev.filter((i) => i !== id) : [...prev, id];
      // Dropping 'other' also drops its text, matching the backend rule.
      const nextOther = next.includes('other') ? otherText : '';
      if (!next.includes('other')) setOtherText('');
      queueSync({ interests: next, otherText: nextOther });
      return next;
    });
  };

  const handleOtherText = (value: string) => {
    setOtherText(value);
    queueSync({ interests: selected, otherText: value });
  };

  // Flush a pending sync when the card unmounts (page navigation) so the
  // debounce window never swallows the final change. Fire-and-forget: the
  // component is gone, so no state updates or toasts.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const pending = pendingRef.current;
      if (pending) {
        updateMyInterests(pending.interests, pending.otherText || undefined).catch(() => {});
      }
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  const atCap = selected.length >= MAX_PICKS;

  return (
    <section
      aria-label="Welcome to Jody"
      style={{
        background: '#ffffff',
        borderRadius: 22,
        padding: '18px 20px',
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: 0,
          background: '#f4f6f3',
          color: '#6c7872',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      <div style={{ fontWeight: 700, color: '#16241d', fontSize: 16, paddingRight: 36 }}>
        {user?.firstName ? `Welcome, ${user.firstName}!` : 'Welcome to Jody!'} What excites you most?
      </div>
      <div style={{ fontSize: 13.5, color: '#6c7872', margin: '4px 0 12px' }}>
        Optional. Pick up to {MAX_PICKS}, it helps us build the right things first.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {INTEREST_OPTIONS.map((opt) => {
          const isSelected = selected.includes(opt.id);
          const disabled = !isSelected && atCap;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              disabled={disabled}
              style={chipStyle(isSelected, disabled)}
            >
              <span aria-hidden>{opt.emoji}</span>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>

      {selected.includes('other') && (
        <textarea
          value={otherText}
          onChange={(e) => handleOtherText(e.target.value)}
          placeholder="Tell us in your own words"
          rows={2}
          maxLength={200}
          style={{
            width: '100%',
            marginTop: 10,
            padding: '8px 12px',
            fontSize: 13.5,
            fontFamily: 'inherit',
            color: '#16241d',
            background: '#f4f6f3',
            border: '1.5px solid rgba(20,40,30,0.10)',
            borderRadius: 10,
            resize: 'none',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      )}

      {saved && (
        <div style={{ fontSize: 12.5, color: '#127a54', fontWeight: 600, marginTop: 10 }}>
          ✓ Saved. You can keep adjusting, or close this card.
        </div>
      )}
    </section>
  );
};

export default InterestsWelcomeCard;
